import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  type TelemetryHealthStatus,
  type TelemetryHealthResult,
  resolveTelemetryHealth,
} from '@/lib/telemetry-health';
import { batchedQuery } from '@/lib/batched-query';

// ─── Types ──────────────────────────────────────────────────────────────

export interface TelemetryHealthRow {
  property_id: string;
  property_name: string;
  org_name: string | null;
  provider_type: string;
  system_id: string | null;
  plant_id: string | null;
  // Polling fields
  last_poll_at: string | null;
  last_poll_status: string | null;
  last_provider_ok_at: string | null;
  last_provider_auth_failed_at: string | null;
  last_non_empty_telemetry_at: string | null;
  consecutive_failures: number;
  backoff_until: string | null;
  // Telemetry stats
  days_with_data_30: number;
  days_with_data_60: number;
  total_daily_summaries: number;
  all_summaries_zero: boolean;
  first_summary_date: string | null;
  latest_summary_date: string | null;
  // Report subscription
  has_report_subscription: boolean;
  report_enabled: boolean;
  // Classification
  health_status: TelemetryHealthStatus;
  recommended_action: string;
}

export interface TelemetryHealthSummary {
  healthy: number;
  stale: number;
  no_recent_data: number;
  never_reported: number;
  provider_error: number;
  zero_data_only: number;
  unknown: number;
  total: number;
}

export interface TelemetryHealthData {
  rows: TelemetryHealthRow[];
  summary: TelemetryHealthSummary;
}

// ─── Fetch ──────────────────────────────────────────────────────────────

async function fetchTelemetryHealth(): Promise<TelemetryHealthData> {
  // 1. All properties with service provider (fleet scope)
  const { data: properties, error: propErr } = await supabase
    .from('properties')
    .select('id, name, service_provider_org_id')
    .not('service_provider_org_id', 'is', null);

  if (propErr) throw propErr;
  if (!properties?.length) return { rows: [], summary: emptySummary() };

  const propIds = properties.map((p) => p.id);

  // 2. Energy systems (polling status fields) — batched for scale safety
  const systems = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('energy_systems')
      .select(`
        id, property_id,
        last_poll_at, last_poll_status,
        last_provider_ok_at, last_provider_auth_failed_at,
        last_non_empty_telemetry_at,
        consecutive_failures, backoff_until
      `)
      .in('property_id', chunk);
    return data ?? [];
  });

  // Map: property_id → system (use first system per property for simplicity)
  const systemByProp = new Map<string, (typeof systems)[number]>();
  for (const s of systems ?? []) {
    if (!systemByProp.has(s.property_id)) {
      systemByProp.set(s.property_id, s);
    }
  }

  // 3. Plants (provider_type) — batched for scale safety
  const plants = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('plants')
      .select('id, property_id, provider_type')
      .in('property_id', chunk);
    return data ?? [];
  });

  const plantByProp = new Map<string, { id: string; provider_type: string }>();
  for (const p of plants) {
    if (!plantByProp.has(p.property_id)) {
      plantByProp.set(p.property_id, { id: p.id, provider_type: p.provider_type });
    }
  }

  // 4. Organizations (for org name display)
  const orgIds = [...new Set(properties.map((p) => p.service_provider_org_id).filter(Boolean))];
  const { data: orgs } = await supabase
    .from('organizations')
    .select('id, name')
    .in('id', orgIds);

  const orgNameById = new Map((orgs ?? []).map((o) => [o.id, o.name]));

  // 5. Daily energy summaries — aggregate per property (last 30d, last 60d, total)
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  // Batched to avoid PostgREST URL limit at scale (1,000+ property IDs)
  const summaries60 = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('daily_energy_summary')
      .select('property_id, date, pv_energy_wh, load_energy_wh, grid_imported_wh, grid_exported_wh')
      .in('property_id', chunk)
      .gte('date', sixtyDaysAgo);
    return data ?? [];
  });

  // Total count (all time) and first date — batched
  const summaryTotals = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('daily_energy_summary')
      .select('property_id, date')
      .in('property_id', chunk)
      .order('date', { ascending: true });
    return data ?? [];
  });

  // Build per-property summary stats
  const summaryStats = new Map<string, {
    days_30: number;
    days_60: number;
    total: number;
    all_zero: boolean;
    first_date: string | null;
    latest_date: string | null;
  }>();

  // Initialize
  for (const pid of propIds) {
    summaryStats.set(pid, { days_30: 0, days_60: 0, total: 0, all_zero: true, first_date: null, latest_date: null });
  }

  // Total counts and first/latest
  for (const row of summaryTotals) {
    const stat = summaryStats.get(row.property_id);
    if (stat) {
      stat.total++;
      if (!stat.first_date || row.date < stat.first_date) stat.first_date = row.date;
      if (!stat.latest_date || row.date > stat.latest_date) stat.latest_date = row.date;
    }
  }

  // 60-day window: count days and check zeros
  for (const row of summaries60) {
    const stat = summaryStats.get(row.property_id);
    if (stat) {
      stat.days_60++;
      if (row.date >= thirtyDaysAgo) stat.days_30++;
      // Check if any non-zero energy
      const totalEnergy = (row.pv_energy_wh ?? 0) + (row.load_energy_wh ?? 0) +
        (row.grid_imported_wh ?? 0) + (row.grid_exported_wh ?? 0);
      if (totalEnergy > 0) stat.all_zero = false;
    }
  }

  // 6. Report subscriptions — batched
  const subs = await batchedQuery(propIds, async (chunk) => {
    const { data } = await supabase
      .from('customer_report_subscriptions')
      .select('property_id, enabled')
      .in('property_id', chunk);
    return data ?? [];
  });

  const subByProp = new Map(subs.map((s) => [s.property_id, s]));

  // 7. Build rows with classification
  const now = Date.now();
  const rows: TelemetryHealthRow[] = properties.map((prop) => {
    const sys = systemByProp.get(prop.id);
    const plant = plantByProp.get(prop.id);
    const stat = summaryStats.get(prop.id)!;
    const sub = subByProp.get(prop.id);

    const healthInput = {
      last_provider_ok_at: sys?.last_provider_ok_at ?? null,
      last_non_empty_telemetry_at: sys?.last_non_empty_telemetry_at ?? null,
      last_poll_at: sys?.last_poll_at ?? null,
      last_poll_status: sys?.last_poll_status ?? null,
      last_provider_auth_failed_at: sys?.last_provider_auth_failed_at ?? null,
      consecutive_failures: sys?.consecutive_failures ?? 0,
      days_with_data_30: stat.days_30,
      total_daily_summaries: stat.total,
      all_summaries_zero: stat.all_zero,
      now,
    };

    const { status, recommended_action }: TelemetryHealthResult = resolveTelemetryHealth(healthInput);

    return {
      property_id: prop.id,
      property_name: prop.name ?? 'Unknown',
      org_name: orgNameById.get(prop.service_provider_org_id) ?? null,
      provider_type: plant?.provider_type ?? 'unknown',
      system_id: sys?.id ?? null,
      plant_id: plant?.id ?? null,
      last_poll_at: sys?.last_poll_at ?? null,
      last_poll_status: sys?.last_poll_status ?? null,
      last_provider_ok_at: sys?.last_provider_ok_at ?? null,
      last_provider_auth_failed_at: sys?.last_provider_auth_failed_at ?? null,
      last_non_empty_telemetry_at: sys?.last_non_empty_telemetry_at ?? null,
      consecutive_failures: sys?.consecutive_failures ?? 0,
      backoff_until: sys?.backoff_until ?? null,
      days_with_data_30: stat.days_30,
      days_with_data_60: stat.days_60,
      total_daily_summaries: stat.total,
      all_summaries_zero: stat.all_zero,
      first_summary_date: stat.first_date,
      latest_summary_date: stat.latest_date,
      has_report_subscription: !!sub,
      report_enabled: sub?.enabled ?? false,
      health_status: status,
      recommended_action,
    };
  });

  // Sort: errors first, then stale, then healthy
  const STATUS_ORDER: Record<TelemetryHealthStatus, number> = {
    provider_error: 0,
    never_reported: 1,
    zero_data_only: 2,
    no_recent_data: 3,
    stale: 4,
    unknown: 5,
    healthy: 6,
  };
  rows.sort((a, b) => STATUS_ORDER[a.health_status] - STATUS_ORDER[b.health_status]);

  // Summary counts
  const summary: TelemetryHealthSummary = {
    healthy: 0, stale: 0, no_recent_data: 0, never_reported: 0,
    provider_error: 0, zero_data_only: 0, unknown: 0, total: rows.length,
  };
  for (const r of rows) {
    summary[r.health_status]++;
  }

  return { rows, summary };
}

function emptySummary(): TelemetryHealthSummary {
  return { healthy: 0, stale: 0, no_recent_data: 0, never_reported: 0, provider_error: 0, zero_data_only: 0, unknown: 0, total: 0 };
}

// ─── Hook ───────────────────────────────────────────────────────────────

export function useAdminTelemetryHealth(enabled: boolean, refetchInterval: number | false) {
  return useQuery({
    queryKey: ['admin-telemetry-health'],
    queryFn: fetchTelemetryHealth,
    enabled,
    refetchInterval,
    staleTime: 60_000,
  });
}
